import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const WeeklyClosing = () => {
    const MySwal = withReactContent(Swal);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);

    // Search and Filter State
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");

    useEffect(() => {
        fetchData();
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await axios.get("https://avastar-pos-backend.onrender.com/api/categories/active");
            setCategories(res.data.filter(c => c.type !== "Menu"));
        } catch (error) {
            console.error(error);
        }
    };

    const fetchData = async () => {
        try {
            const res = await axios.get("https://avastar-pos-backend.onrender.com/api/inventory/products");
            setProducts(res.data);
            setLoading(false);
            
            // initialize all physical counts with current stock, making it easier for users
            // so they only edit what actually changed
            const initialCounts = {};
            res.data.forEach(p => {
                if (p.variants) {
                    p.variants.forEach(v => {
                        initialCounts[v._id] = v.currentStock;
                    });
                }
            });
            setCounts(initialCounts);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    const handleCountChange = (variantId, val, maxAllowed) => {
        if (val === "" || val === "-") {
            setCounts({ ...counts, [variantId]: val });
            return;
        }
        const n = Number(val);
        if (!Number.isFinite(n) || n < 0) return;
        const capped = Math.min(n, maxAllowed);
        setCounts({ ...counts, [variantId]: capped });
    };

    const submitClosing = async () => {
        const payloadCounts = [];
        for (const [variantId, count] of Object.entries(counts)) {
            // Only send if it's a valid number
            if (count !== "" && !isNaN(count)) {
                payloadCounts.push({ variantId, physicalCount: Number(count) });
            }
        }

        if (payloadCounts.length === 0) return;

        const confirm = await MySwal.fire({
            title: "Confirm Closing?",
            text: "This will calculate usage based on the physical count provided and record it.",
            icon: "warning",
            showCancelButton: true
        });

        if (confirm.isConfirmed) {
            try {
                const res = await axios.post("https://avastar-pos-backend.onrender.com/api/inventory/daily-closing", {
                    counts: payloadCounts,
                    notes: `Weekly Closing - ${new Date().toLocaleDateString()}`
                });
                
                MySwal.fire({
                    title: "Success", 
                    text: `Recorded ${res.data.results.length} item updates!`,
                    icon: "success"
                });
                fetchData();
            } catch(e) {
                MySwal.fire("Error", e.response?.data?.message || "Could not complete closing", "error");
            }
        }
    };

    // Filter Logic
    const filteredProducts = products.filter(product => {
        const matchesCategory = selectedCategory === "All" || (product.category?._id === selectedCategory);
        const searchLower = searchQuery.toLowerCase();
        
        const matchesSearch = (
            product.name?.toLowerCase().includes(searchLower) ||
            product.category?.name?.toLowerCase().includes(searchLower) ||
            product.variants?.some(v => 
                v.name?.toLowerCase().includes(searchLower) ||
                Object.values(v.attributes).some(attr => attr?.toLowerCase().includes(searchLower))
            )
        );

        return matchesCategory && matchesSearch;
    });

    return (
        <div className="page-wrapper bg-light bg-opacity-50">
            <div className="content">
                <div className="page-header d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                    <div>
                        <h4 className="fw-bold mb-1">Weekly Closing (Bulk Count)</h4>
                        <p className="text-muted mb-0">Enter this week&apos;s physical stock count to automatically calculate usage.</p>
                    </div>
                    <button className="btn btn-primary fw-bold px-4 py-2 shadow-sm w-100 w-md-auto" onClick={submitClosing}>
                        <i className="fas fa-check-circle me-2"></i> Submit Closing Sheet
                    </button>
                </div>
                
                {/* Filters */}
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-body py-3 d-flex flex-column flex-md-row flex-wrap gap-3 align-items-stretch align-items-md-center">
                        <div className="input-group" style={{ flex: '1 1 300px' }}>
                            <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                            <input 
                                type="text" 
                                className="form-control border-start-0 ps-0" 
                                placeholder="Search by product, variant, or attribute..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div style={{ flex: '1 1 200px' }}>
                            <select 
                                className="form-select" 
                                value={selectedCategory} 
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="All">All Categories</option>
                                {categories.map(c => (
                                    <option key={c._id} value={c._id}>{c.type} - {c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                ) : (
                    <div className="row g-4">
                        {filteredProducts.map(p => (
                            <div key={p._id} className="col-12 col-md-6 mb-2">
                                <div className="card shadow-sm border-0 h-100">
                                    <div className="card-header bg-white border-bottom-0 py-3">
                                        <h5 className="mb-0 fw-bold">{p.name}</h5>
                                        <span className="badge bg-light text-dark">{p.category?.name}</span>
                                    </div>
                                    <div className="card-body p-0">
                                        {p.variants?.length > 0 ? (
                                            <div className="table-responsive">
                                              <table className="table table-borderless table-striped mb-0 text-nowrap">
                                                  <thead className="table-light">
                                                      <tr>
                                                          <th className="ps-3 ps-md-4 py-3">Variant</th>
                                                          <th className="py-3">Expected</th>
                                                          <th className="pe-3 pe-md-4 py-3" style={{minWidth: '160px'}}>Physical Count</th>
                                                      </tr>
                                                  </thead>
                                                  <tbody>
                                                    {p.variants.map(v => (
                                                        <tr key={v._id}>
                                                            <td className="ps-4 fw-medium align-middle">
                                                                {v.name}
                                                                <small className="d-block text-muted">{Object.keys(v.attributes).length ? Object.values(v.attributes).join(' | ') : ""}</small>
                                                            </td>
                                                            <td className="align-middle">
                                                                <span className="badge bg-secondary fs-6 px-3">{v.currentStock} {v.unit}</span>
                                                            </td>
                                                            <td className="pe-4 align-middle">
                                                                <div className="input-group input-group-sm rounded shadow-sm">
                                                                    <input 
                                                                        type="number" 
                                                                        min="0"
                                                                        max={v.currentStock}
                                                                        className="form-control form-control-lg text-center fw-bold text-primary"
                                                                        value={counts[v._id] !== undefined ? counts[v._id] : v.currentStock}
                                                                        onChange={e => handleCountChange(v._id, e.target.value, v.currentStock)}
                                                                        style={{ fontSize: '18px'}}
                                                                    />
                                                                    <span className="input-group-text bg-white">{v.unit}</span>
                                                                </div>
                                                            </td>
                                                      </tr>
                                                  ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        ) : (
                                            <div className="text-muted text-center py-3 small">No variants attached.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {filteredProducts.length === 0 && (
                            <div className="w-100 text-center text-muted p-5">
                                <i className="fas fa-search fs-1 mb-3 text-light"></i>
                                <h5>No items found matching &quot;{searchQuery}&quot;</h5>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
export default WeeklyClosing;
